import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  ViewChild,
  QueryList,
  ViewChildren,
  ComponentFactoryResolver,
} from '@angular/core';
import { FilterMatchMode, MessageService, SelectItem } from 'primeng/api';
import { forkJoin, of, Subscription } from 'rxjs';
import { Table } from 'primeng/table';
import { MatDialog } from '@angular/material/dialog';
import { PercentageDialogComponent, PercentageDialogData } from '../percentage-dialog/percentage-dialog.component';
import { saveAs } from 'file-saver';
import { concatMap, finalize, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import html2pdf from 'html2pdf.js';
import {
  ConfirmDialogComponent,
  FOYDataService,
  getQuestions,
  InformationDialogComponent,
  LanguageService,
  ProgressSpinnerService,
  QuestionAnswerGetResponse,
  QuestionAnswerService,
  QuestionComponent,
  QuestionGroupResponse,
  QuestionListDirective,
  SurveySectionGetResponse,
  SurveyStateService,
  UsersPanelService,
  UserSurveyDataResponse,
} from '../../../../../lib/src/public-api';
// import { FOYDataService } from 'src/app/services/foy-data.service';

@Component({
  selector: 'app-users-panel',
  templateUrl: './users-panel.component.html',
  styleUrls: ['./users-panel.component.scss'],
  standalone: false,
})
export class UsersPanelComponent implements OnInit {
  @Input() surveyId: number;

  @ViewChild('dt') dt: Table;

  @ViewChildren(QuestionListDirective) questionList: QueryList<QuestionListDirective>;
  questionGroups: QuestionGroupResponse[];
  sectionTitleHeaders: string[] = [];

  private kearneyEmailDomain = '@kearney.com';
  usersData: UserSurveyDataResponse[];

  excludeKearneyUsers = false;

  surveyStateLoaded = false;
  sections: SurveySectionGetResponse[];
  displayQuestionnaire = false;

  constructor(
    private usersPanelService: UsersPanelService,
    private surveyStateService: SurveyStateService,
    private progressSpinnerService: ProgressSpinnerService,
    private questionAnswerService: QuestionAnswerService,
    private languageService: LanguageService,
    private dialog: MatDialog,
    private componentFactoryResolver: ComponentFactoryResolver,
    private messageService: MessageService,
    private translate: TranslateService,
    private foyDataService: FOYDataService,
  ) {}

  ngOnInit() {
    this.loadUsers();

    this.surveyStateService.surveyInitialised.subscribe((loaded) => {
      this.surveyStateLoaded = loaded;
      if (loaded) {
        this.sections = this.surveyStateService.GetSurveySections();
      }
    });

    this.questionGroups = this.surveyStateService.getQuestionGroupsInOrder();
  }

  private loadUsers() {
    this.progressSpinnerService.show();
    this.usersPanelService.apiUsersPanelGet$Json({ surveyId: this.surveyId }).subscribe((usersData) => {
      this.usersData = usersData;
      this.progressSpinnerService.hide();
    });
  }

  toggleExcludeKearneyUsers(event) {
    this.excludeKearneyUsers = event.checked;
    if (this.excludeKearneyUsers) {
      // Workaround for filtering not null values
      this.dt.filter(this.kearneyEmailDomain, 'email', FilterMatchMode.NOT_CONTAINS);
    } else {
      // reset submitted filter
      this.dt.filter(null, 'email', null);
    }
  }

  filterChangedInput(property, target) {
    this.dt.filter(target.value, property, 'contains');
  }

  infoClicked(user: UserSurveyDataResponse) {
    var data: PercentageDialogData = {
      filledResponsesPercentage: user.filledResponsesPercentage,
      firstName: user.firstName,
      lastName: user.lastName,
      sections: this.sections,
    };

    this.dialog.open(PercentageDialogComponent, {
      disableClose: false,
      data: data,
    });
  }

  reverseSubmitionClicked(user: UserSurveyDataResponse) {
    this.dialog
      .open(ConfirmDialogComponent, {
        disableClose: false,
        data: {
          message: `Are you sure you want to reverse submition for ${user.firstName} ${user.lastName}?`,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.usersPanelService
            .apiUsersPanelPut({
              userId: user.id,
              surveyId: this.surveyId,
            })
            .subscribe((_) => this.loadUsers());
        }
      });
  }

  filterSubmitted(event) {
    var val = event.checked;
    if (val) {
      // Workaround for filtering not null values
      this.dt.filter('-', 'submissionDate', FilterMatchMode.CONTAINS);
    } else {
      // reset submitted filter
      this.dt.filter(null, 'submissionDate', null);
    }
  }

  downloadData() {
    this.progressSpinnerService.show();

    this.usersPanelService
      .apiUsersPanelExcelGet({ surveyId: this.surveyId })
      .pipe(finalize(() => this.progressSpinnerService.hide()))
      .subscribe((fileBlob) => {
        const currentDate = new Date();
        const shortDate = currentDate.toLocaleDateString('de-DE');
        saveAs(fileBlob, `users_data_${shortDate}.xlsx`);
      });
  }

  private loadQuestionGroups(user: UserSurveyDataResponse, generatePdf: boolean = false) {
    this.displayQuestionnaire = false;
    var questionGroupsRequest =
      this.questionGroups.length > 0
        ? of(this.questionGroups)
        : this.surveyStateService.initSurveyCommand2(user.surveyInstanceId, this.languageService.language);

    questionGroupsRequest
      .pipe(
        tap((data) => (this.questionGroups = this.surveyStateService.getQuestionGroupsInOrder())),
        concatMap(() => {
          return this.foyDataService.updateCurrencyForUser(user.id, user.surveyInstanceId, this.surveyId);
        }),
        concatMap(() => {
          const tasks$ = [];
          this.questionGroups.forEach((qg) => {
            tasks$.push(
              this.questionAnswerService.apiQuestionAnswerGetAnswersForSurveyGet$Json({
                questionGroupId: qg.id,
                surveyInstanceId: user.surveyInstanceId,
                language: this.languageService.language,
                surveyId: this.surveyId,
              }),
            );
          });
          return forkJoin(tasks$);
        }),
      )
      .subscribe(
        (responses: QuestionAnswerGetResponse[]) => {
          this.LoadQuestionnaire(responses);
          if (generatePdf) {
            this.generatePdf(user.id.toString());
          }
          this.progressSpinnerService.hide();
          this.displayQuestionnaire = true;
        },
        (_) => {
          this.progressSpinnerService.hide();
        },
      );
  }

  private LoadQuestionnaire(questionsGroups: QuestionAnswerGetResponse[]) {
    this.sectionTitleHeaders = [];
    var surveySections = this.surveyStateService.GetSurveySections();

    this.questionList.forEach((question, index) => {
      question.viewContainerRef.clear();
      const viewContainerRef = question.viewContainerRef;

      const questionsForSurveyType = this.surveyStateService.GetQuestionsForSurveyType(
        questionsGroups[index].questionListInGroup,
      );

      var questionsInGroup = getQuestions(
        questionsForSurveyType,
        questionsGroups[index].questionAnswerInstanceListInGroup,
      );
      this.sectionTitleHeaders.push(surveySections[index].text);
      questionsInGroup.forEach((question) => {
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(question.component);
        const componentRef = viewContainerRef.createComponent(componentFactory);
        (componentRef.instance as QuestionComponent).questionData = question.questionData;
        (componentRef.instance as QuestionComponent).questionAnswerInstanceList = question.questionAnswerInstanceList;
        (componentRef.instance as QuestionComponent).messageService = this.messageService;
        (componentRef.instance as QuestionComponent).readonly = true;
        (componentRef.instance as QuestionComponent).surveyStateService = this.surveyStateService;
      });
    });
  }

  generatePdf(name: string) {
    var elements = document.querySelectorAll<HTMLElement>('.pdf');

    var array = Array.from(elements);
    // array = array.slice(0, -1);
    var content = this.combineElements(array);

    const options = {
      margin: 10,
      filename: `${name}.pdf`,
      pagebreak: { before: 'app-matrix-numerical-question' },
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 1 },
      jsPDF: { unit: 'mm', format: 'a3', orientation: 'portrait', pagesplit: false },
    };

    var dialogRef = this.dialog.open(InformationDialogComponent, {
      disableClose: false,
      data: {
        message: this.translate.instant('PDF is being generated. This may take a few seconds'),
      },
    });

    setTimeout(() => this.downloadPDF(array, options), 100);
  }

  // private downloadPDF(elements, options) {
  //   let worker = html2pdf()
  //     .set(options)
  //     .from(elements[0])

  //   // TOOD Update v20
  //   if (elements.length > 1) {
  //     worker = worker.toPdf() // worker is now a jsPDF instance

  //     elements.slice(1).forEach((element, index) => {
  //       worker = worker
  //         .get('pdf')
  //         .then(pdf => {
  //           pdf.addPage();
  //           return pdf;
  //         })
  //         .from(element)
  //         .toContainer()
  //         .toCanvas()
  //         .toPdf()
  //     })
  //   }

  //   worker = worker.save()
  // }

  private async downloadPDF(elements, options) {
    let pdf = await html2pdf().set(options).from(elements[0]).toContainer().toCanvas().toPdf().get('pdf');

    if (elements.length > 1) {
      for (const element of elements.slice(1)) {
        pdf.addPage();

        const canvas = await html2pdf().set(options).from(element).toContainer().toCanvas().get('canvas');

        const imgData = canvas.toDataURL('image/jpeg', 0.98);
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth() - 20;
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        pdf.addImage(imgData, 'JPEG', 10, 10, pdfWidth, pdfHeight);
      }
    }

    pdf.save(options.filename);
  }

  private combineElements(elements: HTMLElement[]): HTMLElement {
    const combinedElement = document.createElement('div');

    elements.forEach((element) => {
      combinedElement.appendChild(element.cloneNode(true));
    });

    return combinedElement;
  }
}
